import {
  EventPlaceholderIDs,
  ModuleDisplayTypeEnum,
  ModuleEnum,
  replacePlaceholderValues,
  StagePlaceholderIDs,
} from "@resource/common";
import _ from "lodash";

import {
  ContentModuleForDisplay,
  GuideResolutionModuleContext,
  StageResolutionModuleContext,
} from "../types";

type ModuleInstallation = {
  id: string;
  position: number;
  data: Record<string, unknown>;
  type: string;
};

const resolveModule = (
  module: ModuleInstallation,
  context: GuideResolutionModuleContext,
  stageContext?: Partial<StageResolutionModuleContext>
): ContentModuleForDisplay => {
  const data = module.data as Record<string, unknown>;
  let dataExtras: Record<string, unknown> = {};
  let displayType = module.data.type as string;

  const allInterviews = _.map(context.interviews, (interview) => ({
    ...interview,
    title: replacePlaceholderValues(
      interview.title,
      interview.variableReplacements as Partial<EventPlaceholderIDs>
    ),
    description: replacePlaceholderValues(
      interview.description,
      interview.variableReplacements as Partial<EventPlaceholderIDs>
    ),
  }));

  const stageInterviews = _.filter(allInterviews, {
    atsJobStageId: stageContext?.atsJobStageId,
  });

  const stageVariableReplacements = (stageContext?.variableReplacements ??
    {}) as Partial<StagePlaceholderIDs>;
  const stageCustomVariables = (stageContext?.customData?.customVariables ??
    {}) as Record<string, string>;

  const hiringTeam = stageContext?.customData?.hiringTeam ?? [];

  switch (module.type) {
    case ModuleEnum.Logistics:
      dataExtras = {
        infoArray: data?.logistics ?? [],
      };
      displayType = ModuleDisplayTypeEnum.InfoCards;
      break;
    case ModuleEnum.Assessment:
      dataExtras = {
        descriptionHtml: replacePlaceholderValues(
          (data.descriptionHtml as string | undefined) ?? "",
          stageVariableReplacements
        ),
        assessmentUrl:
          stageCustomVariables["Assessment.Url"] ?? data.defaultAssessmentUrl,
        assessmentLabel:
          stageCustomVariables["Assessment.Label"] ??
          data.defaultAssessmentLabel,
      };
      displayType = ModuleDisplayTypeEnum.Assessment;
      break;
    case ModuleEnum.Offer:
      dataExtras = {
        descriptionHtml: replacePlaceholderValues(
          (data.descriptionHtml as string | undefined) ?? "",
          stageVariableReplacements
        ),
        candidateName: context.candidate.fullName,
        jobName: context.job.name,
        offer: {
          totalComp: stageCustomVariables["Offer.TotalComp"],
          isContractor: stageCustomVariables["Offer.IsContractor"],
          salary: stageCustomVariables["Offer.Salary"],
          optionCount: stageCustomVariables["Offer.OptionCount"],
          bonus: stageCustomVariables["Offer.Bonus"],
          url: stageCustomVariables["Offer.Url"],
          quota: stageCustomVariables["Offer.Quota"],
          commission: stageCustomVariables["Offer.Commission"],
          extras: stageCustomVariables["Offer.Extras"],
          level: stageCustomVariables["Offer.Level"],
        },
      };
      displayType = ModuleDisplayTypeEnum.Offer;
      break;
    case ModuleEnum.GreetingHeader:
      dataExtras = {
        header:
          context.candidate.fullName === "Jane Doe"
            ? "Hi Jane Doe,"
            : `Hi ${context.candidate.firstName},`,
        subheader: context.job.name ?? "",
      };
      displayType = ModuleDisplayTypeEnum.GreetingHeader;
      break;
    case ModuleEnum.Time:
      dataExtras = {
        guideUrl: context.publicUrl,
        companyName: context.organization.name,
        events: stageInterviews,
        addToCalendarEnabled: context.organization.addToCalendarEnabled,
      };
      displayType = ModuleDisplayTypeEnum.Time;
      break;
    case ModuleEnum.Location:
      displayType = ModuleDisplayTypeEnum.Location;
      break;
    case ModuleEnum.Mission:
      dataExtras = {
        description: context.organization.missionStatement,
      };
      displayType = ModuleDisplayTypeEnum.Mission;
      break;
    case ModuleEnum.Values:
      dataExtras = {
        infoArray: context.organization.values,
      };
      displayType = ModuleDisplayTypeEnum.InfoCards;
      break;
    case ModuleEnum.Media:
    case ModuleEnum.TeamPhoto:
      dataExtras = {
        ...data,
        mediaLink: data.mediaLink ?? data.photoUrl,
      };
      displayType = ModuleDisplayTypeEnum.Media;
      break;
    case ModuleEnum.RichText:
      dataExtras = {
        rawHtml: replacePlaceholderValues(
          (data.rawHtml as string | undefined) ?? "",
          stageVariableReplacements
        ),
      };
      displayType = ModuleDisplayTypeEnum.RichText;
      break;
    case ModuleEnum.CandidatePostInterviewFeedback:
      dataExtras = {
        lastEventEndTime: _.last(stageInterviews)?.endTime,
        previouslySubmittedFeedback: !stageContext?.needsFeedback ?? false,
      };
      displayType = ModuleDisplayTypeEnum.CandidatePostInterviewFeedback;
      break;
    case ModuleEnum.Schedule:
      dataExtras = {
        events: stageInterviews,
      };
      displayType = ModuleDisplayTypeEnum.Schedule;
      break;
    case ModuleEnum.InterviewScheduler:
      dataExtras = {
        candidateName: context.candidate.fullName,
        availabilitySlug: stageContext?.schedulerConfig?.availabilitySlug,
        eventTemplateId: stageContext?.schedulerConfig?.eventTemplateId,
        interviewer: _.first(stageContext?.schedulerConfig?.interviewers),
        events: stageInterviews,
      };
      displayType = ModuleDisplayTypeEnum.InterviewScheduler;
      break;
    case ModuleEnum.InterviewTeam:
      dataExtras = {
        interviewers: data.manualSelection
          ? hiringTeam ?? []
          : _(stageInterviews).flatMap("interviewers").uniqBy("id").value(),
      };
      displayType = ModuleDisplayTypeEnum.InterviewTeam;
      break;
    case ModuleEnum.Links:
      dataExtras = {
        defaultImage: context.organization.name,
      };
      displayType = ModuleDisplayTypeEnum.Links;
      break;
    default:
      dataExtras = {};
  }

  return {
    id: module.id,
    data: {
      ...data,
      ...dataExtras,
      displayType,
    },
    displayType,
  };
};

export default resolveModule;
